import React from "react";
import "react-accessible-accordion/dist/fancy-example.css";
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from "react-accessible-accordion";
import Switch from "react-bootstrap-switch";
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";

const numberFields = new Set([
  "amount",
  "amount_in_smallest_unit",
  "display_amount",
  "display_discount",
  "validity_in_months",
  "digicase_id",
  "epaper_share",
  "membership_share",
]);

const staticContentSections = [
  {
    key: "subscription_section",
    title: "Subscription Section",
    fields: [
      ["heading", "Heading"],
      ["subheading", "Subheading"],
      ["already_subscribed_message", "Already Subscribed Message"],
    ],
  },
  {
    key: "subscribe_banner",
    title: "Subscribe Banner",
    fields: [
      ["heading", "Heading"],
      ["subheading", "Subheading"],
      ["description", "Description", true],
      ["cta_button", "CTA Button"],
      ["already_subscribed_message", "Already Subscribed Message"],
    ],
  },
  {
    key: "story_page",
    title: "Story Page",
    fields: [
      ["heading", "Heading"],
      ["subheading", "Subheading"],
      ["description", "Description", true],
    ],
  },
  {
    key: "category_page",
    title: "Category Page",
    fields: [
      ["heading", "Heading"],
      ["subheading", "Subheading"],
      ["cta_button", "CTA Button"],
    ],
  },
  {
    key: "category_page_extend",
    title: "Category Page Extension",
    fields: [
      ["heading", "Heading"],
      ["subheading", "Subheading"],
      ["cta_button", "CTA Button"],
    ],
  },
  {
    key: "switch_to_holiday_plan_popup_web",
    title: "Switch Plan Popup (Web)",
    fields: [
      ["heading", "Heading"],
      ["description", "Description", true],
      ["info_note", "Info Note", true],
      ["primary_cta", "Primary CTA"],
      ["secondary_cta", "Secondary CTA"],
    ],
  },
  {
    key: "extend_popup",
    title: "Extend Popup",
    fields: [
      ["heading", "Heading"],
      ["subheading", "Subheading", true],
      ["subheading_ios", "iOS Subheading", true],
      ["cta_button", "CTA Button"],
      ["cta_button_ios", "iOS CTA Button"],
      ["cta_cancel_button", "Cancel CTA"],
      ["cta_cancel_button_ios", "iOS Cancel CTA"],
    ],
  },
  {
    key: "email_campaign",
    title: "Email Campaign Copy",
    fields: [
      ["sale_label", "Sale Label"],
      ["sale_plan_name", "Sale Plan Name"],
      ["sale_membership_name", "Sale Membership Name"],
      ["sale_benefits_heading", "Sale Benefits Heading"],
      ["sale_benefits_list", "Sale Benefits List (one per line)", true],
      ["activation_subject", "Activation Subject"],
      ["payment_failed_subject", "Payment Failed Subject"],
      ["cancellation_failed_subject", "Cancellation Failed Subject"],
      ["active_subscribers_subject", "Active Subscribers Subject"],
      ["active_subscribers_title", "Mailer Hero Title"],
      ["active_subscribers_subtitle", "Mailer Hero Subtitle (one line per row)", true],
      ["active_subscribers_offer_text", "Mailer Offer Text"],
      ["active_subscribers_section_heading", "Mailer Section Heading"],
      ["active_subscribers_section_description", "Mailer Section Description", true],
      ["renewal_url", "Renew Link (URL)"],
    ],
  },
];

const rowCardStyle = {
  border: "1px solid #e3e7ee",
  borderRadius: "10px",
  padding: "16px",
  marginBottom: "16px",
  background: "#fbfcfe",
};

const sectionHelpStyle = {
  color: "#6c757d",
  fontSize: "12px",
  marginBottom: "12px",
};

const sectionButtonRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "12px",
};

const SortableParent = SortableContainer(({ children }) => (
  <div>{children}</div>
));

const SortableChild = SortableElement(({ children }) => (
  <div style={{ marginBottom: "12px" }}>{children}</div>
));

const DragHandle = SortableHandle(() => (
  <span style={{ marginRight: "10px", cursor: "grab" }}>
    <FontAwesomeIcon icon={faGripVertical} />
  </span>
));

const cloneDeep = (value) => JSON.parse(JSON.stringify(value || {}));

const reorderArray = (items = [], oldIndex, newIndex) => {
  const nextItems = [...items];
  if (
    oldIndex < 0 ||
    newIndex < 0 ||
    oldIndex >= nextItems.length ||
    newIndex >= nextItems.length
  ) {
    return nextItems;
  }

  const [movedItem] = nextItems.splice(oldIndex, 1);
  nextItems.splice(newIndex, 0, movedItem);
  return nextItems;
};

const isObject = (value) => value && typeof value === "object" && !Array.isArray(value);

const ensureConfigShape = (config = {}) => {
  const nextConfig = cloneDeep(config);

  nextConfig.plans = isObject(nextConfig.plans) ? nextConfig.plans : {};
  nextConfig.features = isObject(nextConfig.features) ? nextConfig.features : {};
  nextConfig.static_labels = isObject(nextConfig.static_labels) ? nextConfig.static_labels : {};
  nextConfig.static_content = isObject(nextConfig.static_content) ? nextConfig.static_content : {};
  nextConfig.custom_meta_data = isObject(nextConfig.custom_meta_data) ? nextConfig.custom_meta_data : {};
  nextConfig.show_extended_plan = !!nextConfig.show_extended_plan;

  if (!isObject(nextConfig.static_content.faq_section)) {
    nextConfig.static_content.faq_section = {};
  }
  if (!Array.isArray(nextConfig.static_content.faq_section.faqs)) {
    nextConfig.static_content.faq_section.faqs = [];
  }
  if (!isObject(nextConfig.static_content.email_campaign)) {
    nextConfig.static_content.email_campaign = {};
  }

  return nextConfig;
};

const getNestedValue = (object, path, fallback = "") => {
  const value = path.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), object);
  return value === undefined || value === null ? fallback : value;
};

const setNestedValue = (object, path, value) => {
  let current = object;

  for (let index = 0; index < path.length - 1; index += 1) {
    const key = path[index];
    if (!isObject(current[key])) {
      current[key] = {};
    }
    current = current[key];
  }

  current[path[path.length - 1]] = value;
};

const removeNestedValue = (object, path) => {
  const parent = path.slice(0, -1).reduce((acc, key) => (acc ? acc[key] : undefined), object);
  if (isObject(parent)) {
    delete parent[path[path.length - 1]];
  }
};

const cleanupPlanTree = (plansConfig, currency, tier) => {
  if (plansConfig?.[currency]?.[tier] && Object.keys(plansConfig[currency][tier]).length === 0) {
    delete plansConfig[currency][tier];
  }

  if (plansConfig?.[currency] && Object.keys(plansConfig[currency]).length === 0) {
    delete plansConfig[currency];
  }
};

const getPlanEntries = (config) => {
  const entries = [];

  Object.entries(config.plans || {}).forEach(([currency, tiers]) => {
    Object.entries(tiers || {}).forEach(([tier, periods]) => {
      Object.entries(periods || {}).forEach(([period, plan]) => {
        entries.push({
          currency,
          tier,
          period,
          ...plan,
        });
      });
    });
  });

  return entries;
};

const getFeatureEntries = (config) =>
  Object.entries(config.features || {}).map(([featureKey, feature]) => ({
    featureKey,
    ...feature,
  }));

const positiveNumberFields = new Set([
  "amount",
  "amount_in_smallest_unit",
  "display_amount",
  "display_discount",
  "validity_in_months",
]);

const normalizePlanFieldValue = (field, value) => {
  if (numberFields.has(field)) {
    if (value === "") return "";
    const num = Number(value);
    if (positiveNumberFields.has(field) && num <= 0) return "";
    return num;
  }

  return value;
};

const DraftInput = ({ value: propValue, onCommit, ...inputProps }) => {
  const [draft, setDraft] = React.useState(propValue);

  React.useEffect(() => {
    setDraft(propValue);
  }, [propValue]);

  return (
    <Input
      {...inputProps}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== propValue) {
          onCommit(draft);
        }
      }}
    />
  );
};

const SwitchField = ({ label, value, onChange }) => (
  <div style={{ marginBottom: "14px" }}>
    <Label style={{ display: "block" }}>{label}</Label>
    <Switch
      offColor="success"
      offText={<i className="nc-icon nc-simple-remove" />}
      onColor="success"
      onText={<i className="nc-icon nc-check-2" />}
      value={!!value}
      onChange={(unusedValue, enabled) => onChange(enabled)}
    />
  </div>
);

const SortableAccordionGroup = ({
  items,
  onSortEnd,
  renderHeader,
  renderBody,
  emptyText,
  itemKeyPrefix,
}) => {
  if (!items.length) {
    return <div style={sectionHelpStyle}>{emptyText}</div>;
  }

  return (
    <SortableParent onSortEnd={onSortEnd} useDragHandle lockAxis="y">
      {items.map((item, index) => (
        <SortableChild index={index} key={`${itemKeyPrefix}-${index}`}>
          <Accordion allowZeroExpanded preExpanded={[`${itemKeyPrefix}-${index}`]}>
            <AccordionItem uuid={`${itemKeyPrefix}-${index}`}>
              <AccordionItemHeading>
                <AccordionItemButton>
                  <DragHandle />
                  {renderHeader(item, index)}
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                {renderBody(item, index)}
              </AccordionItemPanel>
            </AccordionItem>
          </Accordion>
        </SortableChild>
      ))}
    </SortableParent>
  );
};

const ProjectSubscriptionPlansEditor = ({
  value,
  onChange,
  onDismissError,
  error,
  meta,
}) => {
  const config = ensureConfigShape(value);
  const planEntries = getPlanEntries(config);
  const featureEntries = getFeatureEntries(config);
  const faqEntries = config.static_content.faq_section.faqs || [];

  const pendingScrollKey = React.useRef(null);
  const [expandedPlanUuid, setExpandedPlanUuid] = React.useState(null);
  const [planAccordionKey, setPlanAccordionKey] = React.useState(0);

  React.useEffect(() => {
    if (!pendingScrollKey.current) return;
    const el = document.querySelector(`[data-plan-key="${pendingScrollKey.current}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      pendingScrollKey.current = null;
    }
  }, [planEntries.length]);

  const updateConfig = (updater) => {
    const nextConfig = ensureConfigShape(cloneDeep(config));
    updater(nextConfig);
    onChange(nextConfig);
  };

  const updateTopLevelField = (path, fieldValue) => {
    updateConfig((nextConfig) => {
      setNestedValue(nextConfig, path, fieldValue);
    });
  };

  const addFeature = () => {
    updateConfig((nextConfig) => {
      const featureKey = `new_feature_${Date.now()}`;
      nextConfig.features[featureKey] = {
        alias: "",
        title: "",
        desc: "",
        image: "",
      };
    });
  };

  const updateFeatureField = (featureEntry, field, fieldValue) => {
    updateConfig((nextConfig) => {
      const currentFeature = cloneDeep(nextConfig.features[featureEntry.featureKey] || {});

      if (field === "featureKey") {
        const nextFeatureKey = fieldValue || featureEntry.featureKey;
        delete nextConfig.features[featureEntry.featureKey];
        nextConfig.features[nextFeatureKey] = currentFeature;
        return;
      }

      currentFeature[field] = fieldValue;
      nextConfig.features[featureEntry.featureKey] = currentFeature;
    });
  };

  const removeFeature = (featureEntry) => {
    updateConfig((nextConfig) => {
      delete nextConfig.features[featureEntry.featureKey];
    });
  };

  const addPlan = () => {
    const uniqueSuffix = Date.now();
    const currency = "INR";
    const tier = `new_tier_${uniqueSuffix}`;
    const period = `new_period_${uniqueSuffix}`;
    pendingScrollKey.current = `${currency}-${tier}-${period}`;
    setExpandedPlanUuid(`plan-${currency}-${tier}-${period}`);
    setPlanAccordionKey((k) => k + 1);

    updateConfig((nextConfig) => {

      if (!nextConfig.plans[currency]) {
        nextConfig.plans[currency] = {};
      }
      nextConfig.plans[currency][tier] = {
        [period]: {
          currency,
          tier,
          period,
          type: "one_time",
          display_name: "",
          plan_heading: "",
          plan_duration_label: "",
          amount: "",
          amount_in_smallest_unit: "",
          display_amount: "",
          display_discount: "",
          validity_in_months: "",
          plan_id: "",
          razorpay_id: "",
          ios_product_id: "",
          android_product_id: "",
          features: "",
          desc: "",
          benefits: [],
          is_recommended: false,
          show_discount: false,
          is_combo_plan: false,
          is_extended: false,
        },
      };
    });
  };

  const removePlan = (entry) => {
    updateConfig((nextConfig) => {
      removeNestedValue(nextConfig, ["plans", entry.currency, entry.tier, entry.period]);
      cleanupPlanTree(nextConfig.plans, entry.currency, entry.tier);
    });
  };

  const updatePlanField = (entry, field, fieldValue) => {
    updateConfig((nextConfig) => {
      const currentPlan = cloneDeep(
        getNestedValue(nextConfig, ["plans", entry.currency, entry.tier, entry.period], {})
      );

      if (["currency", "tier", "period"].includes(field)) {
        const nextEntry = {
          currency: field === "currency" ? fieldValue : entry.currency,
          tier: field === "tier" ? fieldValue : entry.tier,
          period: field === "period" ? fieldValue : entry.period,
        };

        removeNestedValue(nextConfig, ["plans", entry.currency, entry.tier, entry.period]);
        cleanupPlanTree(nextConfig.plans, entry.currency, entry.tier);

        if (!nextConfig.plans[nextEntry.currency]) {
          nextConfig.plans[nextEntry.currency] = {};
        }
        if (!nextConfig.plans[nextEntry.currency][nextEntry.tier]) {
          nextConfig.plans[nextEntry.currency][nextEntry.tier] = {};
        }

        nextConfig.plans[nextEntry.currency][nextEntry.tier][nextEntry.period] = {
          ...currentPlan,
          currency: nextEntry.currency,
          tier: nextEntry.tier,
          period: nextEntry.period,
        };
        return;
      }

      // Only one plan per currency may be recommended: turning one on clears
      // the flag on every other plan within the same currency.
      if (field === "is_recommended" && fieldValue === true) {
        const tiers = nextConfig.plans?.[entry.currency] || {};
        Object.values(tiers).forEach((periods) => {
          Object.values(periods || {}).forEach((plan) => {
            if (plan && typeof plan === "object") {
              plan.is_recommended = false;
            }
          });
        });
      }

      currentPlan[field] = normalizePlanFieldValue(field, fieldValue);
      setNestedValue(nextConfig, ["plans", entry.currency, entry.tier, entry.period], currentPlan);
    });
  };

  const MAX_BENEFITS = 10;

  const addPlanBenefit = (entry) => {
    const currentPlan = getNestedValue(config, ["plans", entry.currency, entry.tier, entry.period], {});
    const benefits = Array.isArray(currentPlan.benefits) ? currentPlan.benefits : [];
    if (benefits.length >= MAX_BENEFITS) return;

    updateConfig((nextConfig) => {
      const plan = cloneDeep(
        getNestedValue(nextConfig, ["plans", entry.currency, entry.tier, entry.period], {})
      );
      const planBenefits = Array.isArray(plan.benefits) ? plan.benefits : [];
      planBenefits.push("");
      plan.benefits = planBenefits;
      setNestedValue(nextConfig, ["plans", entry.currency, entry.tier, entry.period], plan);
    });
  };

  const updatePlanBenefit = (entry, benefitIndex, benefitValue) => {
    updateConfig((nextConfig) => {
      const currentPlan = cloneDeep(
        getNestedValue(nextConfig, ["plans", entry.currency, entry.tier, entry.period], {})
      );
      const benefits = Array.isArray(currentPlan.benefits) ? [...currentPlan.benefits] : [];
      benefits[benefitIndex] = benefitValue;
      currentPlan.benefits = benefits;
      setNestedValue(nextConfig, ["plans", entry.currency, entry.tier, entry.period], currentPlan);
    });
  };

  const removePlanBenefit = (entry, benefitIndex) => {
    updateConfig((nextConfig) => {
      const currentPlan = cloneDeep(
        getNestedValue(nextConfig, ["plans", entry.currency, entry.tier, entry.period], {})
      );
      const benefits = Array.isArray(currentPlan.benefits) ? currentPlan.benefits : [];
      currentPlan.benefits = benefits.filter((unusedItem, index) => index !== benefitIndex);
      setNestedValue(nextConfig, ["plans", entry.currency, entry.tier, entry.period], currentPlan);
    });
  };

  const movePlanBenefit = (entry, oldIndex, newIndex) => {
    updateConfig((nextConfig) => {
      const currentPlan = cloneDeep(
        getNestedValue(nextConfig, ["plans", entry.currency, entry.tier, entry.period], {})
      );
      const benefits = Array.isArray(currentPlan.benefits) ? currentPlan.benefits : [];
      currentPlan.benefits = reorderArray(benefits, oldIndex, newIndex);
      setNestedValue(nextConfig, ["plans", entry.currency, entry.tier, entry.period], currentPlan);
    });
  };

  const addFaq = () => {
    updateConfig((nextConfig) => {
      nextConfig.static_content.faq_section.faqs.push({
        question: "",
        answer: "",
      });
    });
  };

  const updateFaq = (faqIndex, field, fieldValue) => {
    updateConfig((nextConfig) => {
      const faqs = Array.isArray(nextConfig.static_content.faq_section.faqs)
        ? [...nextConfig.static_content.faq_section.faqs]
        : [];
      faqs[faqIndex] = {
        ...(faqs[faqIndex] || {}),
        [field]: fieldValue,
      };
      nextConfig.static_content.faq_section.faqs = faqs;
    });
  };

  const removeFaq = (faqIndex) => {
    updateConfig((nextConfig) => {
      nextConfig.static_content.faq_section.faqs =
        nextConfig.static_content.faq_section.faqs.filter((unusedItem, index) => index !== faqIndex);
    });
  };

  const moveFaq = (oldIndex, newIndex) => {
    updateConfig((nextConfig) => {
      nextConfig.static_content.faq_section.faqs = reorderArray(
        nextConfig.static_content.faq_section.faqs,
        oldIndex,
        newIndex
      );
    });
  };

  return (
    <div>
      <div style={sectionHelpStyle}>
        Structured editor for pricing, copy, features, and FAQs. Changes here power the project-specific membership plans returned by the API.
      </div>
      <div style={sectionHelpStyle}>
        Custom config: {meta?.hasCustomConfig ? "Yes" : "No"}
        {meta?.updatedAt ? ` | Last updated: ${new Date(meta.updatedAt).toLocaleString()}` : ""}
        {` | Plans: ${planEntries.length} | Features: ${featureEntries.length}`}
      </div>
      {error ? (
        <div
          className="text-danger"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "#fff3f3",
            border: "1px solid #f5c6cb",
            borderRadius: "6px",
            padding: "10px 14px",
            marginBottom: "12px",
            whiteSpace: "pre-line",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <span>{error}</span>
          {onDismissError ? (
            <button
              type="button"
              onClick={onDismissError}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "16px",
                lineHeight: 1,
                padding: "0 0 0 12px",
                color: "#721c24",
              }}
              aria-label="Dismiss error"
            >
              &times;
            </button>
          ) : null}
        </div>
      ) : null}

      <Accordion allowMultipleExpanded allowZeroExpanded preExpanded={["global", "plans"]}>
        <AccordionItem uuid="global">
          <AccordionItemHeading>
            <AccordionItemButton>Global Settings</AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <div style={rowCardStyle}>
              <SwitchField
                label="Show extended plan page"
                value={config.show_extended_plan}
                onChange={(enabled) => updateTopLevelField(["show_extended_plan"], enabled)}
              />
            </div>
          </AccordionItemPanel>
        </AccordionItem>

        <AccordionItem uuid="hero">
          <AccordionItemHeading>
            <AccordionItemButton>Static Labels</AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <div style={rowCardStyle}>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Title</Label>
                    <Input
                      value={getNestedValue(config, ["static_labels", "title"])}
                      onChange={(event) => updateTopLevelField(["static_labels", "title"], event.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Subtitle</Label>
                    <Input
                      value={getNestedValue(config, ["static_labels", "subtitle"])}
                      onChange={(event) => updateTopLevelField(["static_labels", "subtitle"], event.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Primary Button Text</Label>
                    <Input
                      value={getNestedValue(config, ["static_labels", "button_text"])}
                      onChange={(event) => updateTopLevelField(["static_labels", "button_text"], event.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label>Description</Label>
                    <Input
                      type="textarea"
                      rows="3"
                      value={getNestedValue(config, ["static_labels", "desc"])}
                      onChange={(event) => updateTopLevelField(["static_labels", "desc"], event.target.value)}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </div>
          </AccordionItemPanel>
        </AccordionItem>

        <AccordionItem uuid="seo">
          <AccordionItemHeading>
            <AccordionItemButton>SEO Metadata</AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <div style={rowCardStyle}>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>SEO Title</Label>
                    <Input
                      value={getNestedValue(config, ["custom_meta_data", "title"])}
                      onChange={(event) => updateTopLevelField(["custom_meta_data", "title"], event.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label>SEO Description</Label>
                    <Input
                      type="textarea"
                      rows="3"
                      value={getNestedValue(config, ["custom_meta_data", "description"])}
                      onChange={(event) => updateTopLevelField(["custom_meta_data", "description"], event.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label>SEO Keywords</Label>
                    <Input
                      type="textarea"
                      rows="2"
                      value={getNestedValue(config, ["custom_meta_data", "keywords"])}
                      onChange={(event) => updateTopLevelField(["custom_meta_data", "keywords"], event.target.value)}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </div>
          </AccordionItemPanel>
        </AccordionItem>

        <AccordionItem uuid="features">
          <AccordionItemHeading>
            <AccordionItemButton>Features</AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <div style={rowCardStyle}>
              <div style={sectionButtonRowStyle}>
                <Label style={{ marginBottom: 0 }}><b>Feature Highlights</b></Label>
                <Button color="info" size="sm" type="button" onClick={addFeature}>
                  Add Feature
                </Button>
              </div>
              {featureEntries.length === 0 ? (
                <div style={sectionHelpStyle}>No features added yet.</div>
              ) : null}
              {featureEntries.map((featureEntry, index) => (
                <div key={`${featureEntry.featureKey}-${index}`} style={rowCardStyle}>
                  <Row>
                    <Col md="3">
                      <FormGroup>
                        <Label>Feature Key</Label>
                        <Input
                          value={featureEntry.featureKey}
                          onChange={(event) => updateFeatureField(featureEntry, "featureKey", event.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Alias</Label>
                        <Input
                          value={featureEntry.alias || ""}
                          onChange={(event) => updateFeatureField(featureEntry, "alias", event.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Title</Label>
                        <Input
                          value={featureEntry.title || ""}
                          onChange={(event) => updateFeatureField(featureEntry, "title", event.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="2" style={{ display: "flex", alignItems: "end" }}>
                      <Button color="danger" size="sm" type="button" onClick={() => removeFeature(featureEntry)}>
                        Remove
                      </Button>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <Label>Image URL</Label>
                        <Input
                          value={featureEntry.image || ""}
                          onChange={(event) => updateFeatureField(featureEntry, "image", event.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <Label>Description</Label>
                        <Input
                          type="textarea"
                          rows="2"
                          value={featureEntry.desc || ""}
                          onChange={(event) => updateFeatureField(featureEntry, "desc", event.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          </AccordionItemPanel>
        </AccordionItem>

        <AccordionItem uuid="plans">
          <AccordionItemHeading>
            <AccordionItemButton>Plans</AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <div style={rowCardStyle}>
              <div style={sectionButtonRowStyle}>
                <Label style={{ marginBottom: 0 }}><b>Subscription Plans</b></Label>
                <Button color="info" size="sm" type="button" onClick={addPlan}>
                  Add Plan
                </Button>
              </div>
              <div style={sectionHelpStyle}>
                Each plan stays inside its own accordion card so product teams can focus on one plan at a time.
              </div>

              {planEntries.length ? (
                <Accordion
                  key={planAccordionKey}
                  allowZeroExpanded
                  preExpanded={expandedPlanUuid ? [expandedPlanUuid] : []}
                  onChange={(expanded) => setExpandedPlanUuid(expanded[0] || null)}
                >
                  {planEntries.map((entry, index) => (
                    <div key={`plan-${entry.currency}-${entry.tier}-${entry.period}-${index}`} data-plan-key={`${entry.currency}-${entry.tier}-${entry.period}`}>
                    <AccordionItem uuid={`plan-${entry.currency}-${entry.tier}-${entry.period}`}>
                      <AccordionItemHeading>
                        <AccordionItemButton>
                          {entry.plan_heading || entry.display_name || `Plan ${index + 1}`} ({entry.currency}/{entry.tier}/{entry.period})
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel>
                        <div style={rowCardStyle}>
                          <div style={sectionButtonRowStyle}>
                            <SwitchField
                              label="Plan Active"
                              value={entry.is_active !== false}
                              onChange={(enabled) => updatePlanField(entry, "is_active", enabled)}
                            />
                            <Button color="danger" size="sm" type="button" onClick={() => removePlan(entry)}>
                              Remove Plan
                            </Button>
                          </div>
                          <Row>
                            <Col md="4">
                              <FormGroup>
                                <Label>Currency</Label>
                                <Input
                                  type="select"
                                  value={entry.currency}
                                  onChange={(event) => updatePlanField(entry, "currency", event.target.value)}
                                >
                                  <option value="INR">INR</option>
                                  <option value="USD">USD</option>
                                </Input>
                              </FormGroup>
                            </Col>
                            <Col md="4">
                              <FormGroup>
                                <Label>Tier</Label>
                                <DraftInput
                                  value={entry.tier || ""}
                                  onCommit={(value) => {
                                    setExpandedPlanUuid(`plan-${entry.currency}-${value}-${entry.period}`);
                                    setPlanAccordionKey((k) => k + 1);
                                    updatePlanField(entry, "tier", value);
                                  }}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="4">
                              <FormGroup>
                                <Label>Period</Label>
                                <DraftInput
                                  value={entry.period || ""}
                                  onCommit={(value) => {
                                    setExpandedPlanUuid(`plan-${entry.currency}-${entry.tier}-${value}`);
                                    setPlanAccordionKey((k) => k + 1);
                                    updatePlanField(entry, "period", value);
                                  }}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="4">
                              <FormGroup>
                                <Label>Display Name</Label>
                                <Input
                                  value={entry.display_name || ""}
                                  maxLength={100}
                                  onChange={(event) => updatePlanField(entry, "display_name", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="4">
                              <FormGroup>
                                <Label>Plan Heading</Label>
                                <Input
                                  value={entry.plan_heading || ""}
                                  maxLength={100}
                                  onChange={(event) => updatePlanField(entry, "plan_heading", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="4">
                              <FormGroup>
                                <Label>Plan Duration Label</Label>
                                <Input
                                  value={entry.plan_duration_label || ""}
                                  maxLength={60}
                                  onChange={(event) => updatePlanField(entry, "plan_duration_label", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <FormGroup>
                                <Label>Type</Label>
                                <Input
                                  type="select"
                                  value={entry.type || "one_time"}
                                  onChange={(event) => updatePlanField(entry, "type", event.target.value)}
                                >
                                  <option value="one_time">One Time</option>
                                  <option value="recurring">Recurring</option>
                                </Input>
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <FormGroup>
                                <Label>Plan ID</Label>
                                <Input
                                  value={entry.plan_id || ""}
                                  onChange={(event) => updatePlanField(entry, "plan_id", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <FormGroup>
                                <Label>Razorpay ID</Label>
                                <Input
                                  value={entry.razorpay_id || ""}
                                  onChange={(event) => updatePlanField(entry, "razorpay_id", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <FormGroup>
                                <Label>GA4 Plan Type</Label>
                                <Input
                                  value={entry.ga4_plan_type || ""}
                                  onChange={(event) => updatePlanField(entry, "ga4_plan_type", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <FormGroup>
                                <Label>Amount</Label>
                                <Input
                                  type="number"
                                  step="any"
                                  min="0.01"
                                  value={entry.amount ?? ""}
                                  onChange={(event) => updatePlanField(entry, "amount", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <FormGroup>
                                <Label>Amount in Smallest Unit</Label>
                                <Input
                                  type="number"
                                  step="1"
                                  min="1"
                                  value={entry.amount_in_smallest_unit ?? ""}
                                  onChange={(event) => updatePlanField(entry, "amount_in_smallest_unit", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <FormGroup>
                                <Label>Display Amount</Label>
                                <Input
                                  type="number"
                                  step="any"
                                  min="0.01"
                                  value={entry.display_amount ?? ""}
                                  onChange={(event) => updatePlanField(entry, "display_amount", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <FormGroup>
                                <Label>Display Discount</Label>
                                <Input
                                  type="number"
                                  step="any"
                                  min="0.01"
                                  value={entry.display_discount ?? ""}
                                  onChange={(event) => updatePlanField(entry, "display_discount", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <FormGroup>
                                <Label>Validity in Months</Label>
                                <Input
                                  type="number"
                                  step="1"
                                  min="1"
                                  value={entry.validity_in_months ?? ""}
                                  onChange={(event) => updatePlanField(entry, "validity_in_months", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <FormGroup>
                                <Label>Digicase ID</Label>
                                <Input
                                  type="number"
                                  step="1"
                                  value={entry.digicase_id ?? ""}
                                  onChange={(event) => updatePlanField(entry, "digicase_id", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <FormGroup>
                                <Label>Epaper Share</Label>
                                <Input
                                  type="number"
                                  step="any"
                                  value={entry.epaper_share ?? ""}
                                  onChange={(event) => updatePlanField(entry, "epaper_share", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <FormGroup>
                                <Label>Membership Share</Label>
                                <Input
                                  type="number"
                                  step="any"
                                  value={entry.membership_share ?? ""}
                                  onChange={(event) => updatePlanField(entry, "membership_share", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="4">
                              <FormGroup>
                                <Label>iOS Product ID</Label>
                                <Input
                                  value={entry.ios_product_id || ""}
                                  onChange={(event) => updatePlanField(entry, "ios_product_id", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="4">
                              <FormGroup>
                                <Label>Android Product ID</Label>
                                <Input
                                  value={entry.android_product_id || ""}
                                  onChange={(event) => updatePlanField(entry, "android_product_id", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="4">
                              <FormGroup>
                                <Label>Feature Aliases (comma separated)</Label>
                                <Input
                                  value={entry.features || ""}
                                  onChange={(event) => updatePlanField(entry, "features", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="12">
                              <FormGroup>
                                <Label>Plan Description</Label>
                                <Input
                                  type="textarea"
                                  rows="3"
                                  value={entry.desc || ""}
                                  onChange={(event) => updatePlanField(entry, "desc", event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="3">
                              <SwitchField
                                label="Show Discount"
                                value={!!entry.show_discount}
                                onChange={(enabled) => updatePlanField(entry, "show_discount", enabled)}
                              />
                            </Col>
                            <Col md="3">
                              <SwitchField
                                label="Recommended"
                                value={!!entry.is_recommended}
                                onChange={(enabled) => updatePlanField(entry, "is_recommended", enabled)}
                              />
                            </Col>
                            <Col md="3">
                              <SwitchField
                                label="Combo Plan"
                                value={!!entry.is_combo_plan}
                                onChange={(enabled) => updatePlanField(entry, "is_combo_plan", enabled)}
                              />
                            </Col>
                            <Col md="3">
                              <SwitchField
                                label="Extended Plan"
                                value={!!entry.is_extended}
                                onChange={(enabled) => updatePlanField(entry, "is_extended", enabled)}
                              />
                            </Col>
                            <Col md="12">
                              <div style={sectionButtonRowStyle}>
                                <Label style={{ marginBottom: 0 }}>
                                  <b>Benefits</b>
                                  <span style={{ ...sectionHelpStyle, marginLeft: "8px", marginBottom: 0 }}>
                                    ({Array.isArray(entry.benefits) ? entry.benefits.length : 0}/{MAX_BENEFITS})
                                  </span>
                                </Label>
                                <Button
                                  color="secondary"
                                  size="sm"
                                  type="button"
                                  disabled={Array.isArray(entry.benefits) && entry.benefits.length >= MAX_BENEFITS}
                                  onClick={() => addPlanBenefit(entry)}
                                >
                                  Add Benefit
                                </Button>
                              </div>
                              <SortableAccordionGroup
                                items={Array.isArray(entry.benefits) ? entry.benefits : []}
                                onSortEnd={({ oldIndex, newIndex }) => movePlanBenefit(entry, oldIndex, newIndex)}
                                itemKeyPrefix={`benefit-${entry.currency}-${entry.tier}-${entry.period}`}
                                emptyText="No benefits added yet."
                                renderHeader={(benefit, benefitIndex) => benefit || `Benefit ${benefitIndex + 1}`}
                                renderBody={(benefit, benefitIndex) => (
                                  <div style={rowCardStyle}>
                                    <FormGroup>
                                      <Label>Benefit Text</Label>
                                      <Input
                                        value={benefit || ""}
                                        maxLength={150}
                                        onChange={(event) => updatePlanBenefit(entry, benefitIndex, event.target.value)}
                                      />
                                    </FormGroup>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      type="button"
                                      onClick={() => removePlanBenefit(entry, benefitIndex)}
                                    >
                                      Remove Benefit
                                    </Button>
                                  </div>
                                )}
                              />
                            </Col>
                          </Row>
                        </div>
                      </AccordionItemPanel>
                    </AccordionItem>
                    </div>
                  ))}
                </Accordion>
              ) : (
                <div style={sectionHelpStyle}>No plans added yet.</div>
              )}
            </div>
          </AccordionItemPanel>
        </AccordionItem>

        <AccordionItem uuid="static-content">
          <AccordionItemHeading>
            <AccordionItemButton>Static Content</AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <div style={rowCardStyle}>
              <Accordion allowMultipleExpanded allowZeroExpanded>
                {staticContentSections.map((section) => (
                  <AccordionItem uuid={`content-${section.key}`} key={section.key}>
                    <AccordionItemHeading>
                      <AccordionItemButton>{section.title}</AccordionItemButton>
                    </AccordionItemHeading>
                    <AccordionItemPanel>
                      <div style={rowCardStyle}>
                        <Row>
                          {section.fields.map(([fieldKey, label, isTextarea]) => (
                            <Col md="6" key={`${section.key}-${fieldKey}`}>
                              <FormGroup>
                                <Label>{label}</Label>
                                <Input
                                  type={isTextarea ? "textarea" : "text"}
                                  rows={isTextarea ? 3 : undefined}
                                  value={getNestedValue(config, ["static_content", section.key, fieldKey])}
                                  onChange={(event) => updateTopLevelField(["static_content", section.key, fieldKey], event.target.value)}
                                />
                              </FormGroup>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </AccordionItemPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </AccordionItemPanel>
        </AccordionItem>

        <AccordionItem uuid="faqs">
          <AccordionItemHeading>
            <AccordionItemButton>FAQs</AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <div style={rowCardStyle}>
              <div style={sectionButtonRowStyle}>
                <Label style={{ marginBottom: 0 }}><b>FAQ Section</b></Label>
                <Button color="info" size="sm" type="button" onClick={addFaq}>
                  Add FAQ
                </Button>
              </div>
              <FormGroup>
                <Label>FAQ Section Heading</Label>
                <Input
                  value={getNestedValue(config, ["static_content", "faq_section", "heading"])}
                  onChange={(event) => updateTopLevelField(["static_content", "faq_section", "heading"], event.target.value)}
                />
              </FormGroup>
              <SortableAccordionGroup
                items={faqEntries}
                onSortEnd={({ oldIndex, newIndex }) => moveFaq(oldIndex, newIndex)}
                itemKeyPrefix="faq"
                emptyText="No FAQs added yet."
                renderHeader={(faq, faqIndex) => faq.question || `FAQ ${faqIndex + 1}`}
                renderBody={(faq, faqIndex) => (
                  <div style={rowCardStyle}>
                    <FormGroup>
                      <Label>Question</Label>
                      <Input
                        value={faq.question || ""}
                        onChange={(event) => updateFaq(faqIndex, "question", event.target.value)}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Answer</Label>
                      <Input
                        type="textarea"
                        rows="3"
                        value={faq.answer || ""}
                        onChange={(event) => updateFaq(faqIndex, "answer", event.target.value)}
                      />
                    </FormGroup>
                    <Button color="danger" size="sm" type="button" onClick={() => removeFaq(faqIndex)}>
                      Remove FAQ
                    </Button>
                  </div>
                )}
              />
            </div>
          </AccordionItemPanel>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default ProjectSubscriptionPlansEditor;
